import { useEffect, useState } from 'react';
import { Moment } from 'moment-timezone';
import { AlertDto, convertToNewBookingAlerts } from '@spike/new-bookings-action';
import { useApiClientWrapper } from 'hooks';
import { SessionAddressOverride } from 'model/NewMultipleBooking';
import { RoutedBookingAlerts, routeBookingAlerts } from './bookingAlerts';

const ALERTS_URL = 'check_alerts';
const DEBOUNCE_MS = 400;

// One selected session to check, identified by `key` so its result lands on its own card.
export interface SessionAlertSlot {
    key: string;
    from: Moment;
    staffId: number;
}

interface SessionAlertsInput {
    clientId?: number;
    petId?: number;
    serviceId?: number;
    appointmentId?: number;
    addressOverride?: SessionAddressOverride;
    slots: Array<SessionAlertSlot>;
    enabled: boolean;
}

// Keyed by `SessionAlertSlot.key`; a slot is absent until its check answers.
export type SessionAlerts = Record<string, RoutedBookingAlerts>;

// Several selected sessions: `check_alerts` has no batched form, so each session is checked on
// its own, in parallel, with its start and staff. The shared alerts slice is left alone because
// it serves the single-date check and keeps only one response. Every selection change re-checks
// the whole set after a short pause, and answers from an earlier selection are dropped.
export const useSessionAlerts = ({
    clientId,
    petId,
    serviceId,
    appointmentId,
    addressOverride,
    slots,
    enabled
}: SessionAlertsInput): SessionAlerts => {
    const apiClientWrapper = useApiClientWrapper();
    const [alerts, setAlerts] = useState<SessionAlerts>({});

    const active = enabled && Boolean(clientId) && Boolean(petId) && slots.length > 0;
    const validSlots = slots.filter(slot => slot.from.isValid() && slot.staffId);
    const slotsKey = validSlots
        .map(slot => `${slot.key}@${slot.from.clone().tz('utc').format()}#${slot.staffId}`)
        .join(',');
    const addressKey = addressOverride
        ? `${addressOverride.latitude ?? ''}|${addressOverride.longitude ?? ''}|${addressOverride.zipcode ?? ''}`
        : '';

    useEffect(() => {
        if (!active || validSlots.length === 0) {
            setAlerts({});
            return;
        }

        // Cards no longer selected lose their alerts right away; the rest keep theirs until
        // the new answers arrive, so the list does not flicker.
        const keys = new Set(validSlots.map(slot => slot.key));
        setAlerts(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => keys.has(key))));

        let stale = false;
        const marketplaceId = apiClientWrapper.marketplaceId;
        const optionalParams = [
            serviceId ? `&service_id=${serviceId}` : '',
            appointmentId ? `&appointment_id=${appointmentId}` : '',
            addressOverride?.latitude != null ? `&latitude=${addressOverride.latitude}` : '',
            addressOverride?.longitude != null ? `&longitude=${addressOverride.longitude}` : '',
            addressOverride?.zipcode ? `&zipcode=${encodeURIComponent(addressOverride.zipcode)}` : ''
        ].join('');

        const timer = setTimeout(() => {
            validSlots.forEach(slot => {
                const url =
                    `${ALERTS_URL}?marketplace_id=${marketplaceId}&customer_id=${clientId}&pet_id=${petId}` +
                    `&appointment_date_time=${encodeURIComponent(slot.from.clone().tz('utc').format())}` +
                    `&recurring=false&staff_id=${slot.staffId}${optionalParams}`;

                apiClientWrapper.apiClient
                    .get<Array<AlertDto>>(url, apiClientWrapper.requestConfig)
                    .then(response => routeBookingAlerts(convertToNewBookingAlerts(response.data ?? [])))
                    // Alerts are advisory: a failed check leaves the card without them.
                    .catch(() => routeBookingAlerts([]))
                    .then(routed => {
                        if (stale) return;
                        setAlerts(prev => ({ ...prev, [slot.key]: routed }));
                    });
            });
        }, DEBOUNCE_MS);

        return () => {
            stale = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- slotsKey and addressKey stand for slots and addressOverride; apiClientWrapper is unstable by design
    }, [active, slotsKey, clientId, petId, serviceId, appointmentId, addressKey]);

    return active ? alerts : {};
};

export default useSessionAlerts;
